import { reactive, watch, ref, nextTick } from 'vue'
import type { TokenPricesHistoryJSONResponse, MarketSchema, EventSchema, EventPriceData, MatchingEvent } from './types/Polymarket';
import apiClient from './utils/axios';
import { jwtDecode } from 'jwt-decode';
import type { TokenPayload } from './types/TokenPayload';
import _ from 'lodash';
import type { DialogConfig } from './types/Dialog';
import { breakpointsTailwind, useBreakpoints, useDebounceFn } from '@vueuse/core'

import confirmAuth from './auth';
import type { GetWatchListJSONResponse, WatchListWithId, CreateWatchListJSONResponse, WatchlistQuery, EventsMap } from './types/Watchlist';
import type { SubscriptionResponse } from './types/Subscriptions';
import type { EventStatusNotificationResponse, GetNotificationsJSONResponse, NotificationResponse, PriceNotificationResponse } from './types/Notifications';
import { NotificationChannelType } from './types/Notifications';
import { SubscriptionStatusReason, SubscriptionScope } from './types/Subscriptions';
import router from './router';
import ms from 'ms';
import { AxiosError } from 'axios';
import * as Sentry from "@sentry/vue";

const breakpoints = useBreakpoints(breakpointsTailwind)
export const isMobile = () => breakpoints.smallerOrEqual('sm').value

const DEFAULT_STORE_SETTINGS = {
  graphs: [],

  // Global User State
  email: '',
  id: '',
  telegramNonce: '',
  telegramChatId: null,
  paid: false,
  isAuthenticated: false,
  isLoggedOut: false,
  isLoggedIn: false,
  failedToVerifyLoginLink: false,
  verifyLicenseKeyModal: false,
  showWelcomeModal: false,
  freeLimitHit: false,

  // Global Watchlist State, including current watchlist
  watchLists: [] as WatchListWithId[],
  currentWatchListId: null as string | null,
  loadingWatchlistId: null as string | null,
  currentWatchList: {} as WatchListWithId,

  // Global Subscription State
  nevuaSubscriptions: [] as SubscriptionResponse[],

  // System (dialog) and price notification state
  dialogs: [] as DialogConfig[],
  notifications: [] as NotificationResponse[],
  showNotifications: isMobile() ? false : true,
  notificationsManuallyToggled: false,
  notificationTimeout: 5000,

  // Subscriptions Dialog state
  showSubscriptionsDialog: false,
  subscriptionScope: null as SubscriptionScope | null,
  subscriptionTargetTitle: null as string | null,
  eventsToCreateAlertFor: null as Record<string, any> | null,
  listAlertsForMarketId: null as string | null,
  listAlertsForEventId: null as string | null,
  listAlertsForWatchlistId: null as string | null,
  subscriptionToEdit: null as SubscriptionResponse | null,
  subscriptionIdToDelete: null as string | null,
  showAlertListTabFirst: false,
  isSubscriptionDeleteModalOpen: false,
  subscriptionPrice: null as number | null,

  // UI state
  isSidebarOpen: isMobile() ? false : true,

  // Table-related state
  columns: [
    { key: 'title', label: 'Event', sortable: true, visible: true },
    { key: 'price', label: 'Chance %', sortable: false, visible: true },
    { key: 'trend', label: 'Trend', sortable: false, visible: true },
    { key: 'volume', label: 'Volume', sortable: true, visible: true },
    { key: 'createdAt', label: 'Created', sortable: true, visible: true },
    { key: 'endDate', label: 'Closes', sortable: true, visible: true },
    // { key: 'provider', label: 'Provider', sortable: false, visible: false },
    { key: 'lastUpdated', label: 'Last_Checked', sortable: false, visible: true },
  ],

  tableState: {
    name: 'Events',
    rowCount: 0,
    events: [] as TokenPricesHistoryJSONResponse,
    multiMarketEvents: {} as Record<string, { expanded: boolean }>,
    updatedRowKeys: {} as Record<string, boolean>,
    sortOrder: {
      title: null,
      volume: null,
      createdAt: 'desc',
      endDate: null,
    },
    viewMode: 'table', // ['table', 'tiles']
  },
}

// Periodically mark these as read in API
const notificationIdsToMarkAsRead: string[] = []

export const store = reactive(_.cloneDeep(DEFAULT_STORE_SETTINGS))

export function setTableState(data, title, listId) {
  // Reset the state first
  store.tableState.events = [];
  store.tableState.rowCount = 0;
  store.tableState.multiMarketEvents = {} as Record<string, { expanded: boolean }>;

  if (title) {
    store.tableState.name = title;
  }

  if (data.length === 0) {
    return
  }

  let expandMultiMarketEventsTable = false;
  if (listId && store.watchLists) {
    const watchList = store.watchLists.find(item => item.id === listId);
    if (watchList?.tableSettings) {
      expandMultiMarketEventsTable = watchList.tableSettings.expandMultiMarketEventsTable;
    }
  }

  store.tableState.events = data;
  store.tableState.rowCount = data.length;
  store.tableState.multiMarketEvents = data
    .filter(event => event.nevuaMultipleMarkets)
    .reduce((acc, event) => {
      acc[event.id] = { "expanded": expandMultiMarketEventsTable };
      return acc;
    }, {});


  if (listId) {
    let filteredSubscriptions = store.nevuaSubscriptions.filter(sub => sub.watchlistId === listId);
    for (const subscription of filteredSubscriptions) {
      addSubscriptionToTableState(subscription, listId);
    }
  }
}

export const createWatchListInAPI = useDebounceFn(async (watchList: WatchListWithId): Promise<string | null> => {
  try {
    const response = await apiClient.post('https://api.nevua.markets/watchlists/create', {
      watchList: _.omit(watchList, 'id')
    });

    if (response.status !== 200) {
      return null;
    }

    const data: CreateWatchListJSONResponse = response.data;

    return data.watchListId;
  } catch (error: any) {
    if (error?.response?.status === 403) {
      store.freeLimitHit = true;
    }
    Sentry.setExtra('method', 'createWatchListInAPI');
    Sentry.setExtra('watchListId', watchList.id);
    Sentry.captureException(error);
    return null;
  }
}, 2000);

export const updateWatchList = useDebounceFn(async (watchListId: string, watchList: Partial<WatchListWithId>) => {
  
  if (!store.currentWatchListId || !store.isAuthenticated) {
    console.warn('Not authenticated, updating watchlist locally')
    return true;
  }

  // Save to API
  try {
    const response = await apiClient.patch('https://api.nevua.markets/watchlists/update', {
      watchListId: watchListId,
      watchList: _.omit(watchList, 'id')
    });

    if (response.status !== 200) {
      throw new Error('Failed to update watchlist');
    }
  } catch (error) {
    Sentry.setExtra('method', 'updateWatchList');
    Sentry.setExtra('watchListId', watchListId);
    Sentry.captureException(error);
    return false;
  }
  return true;
}, 300);

// Fetch watchlists from API, Will reset store.watchLists
export async function fetchWatchLists() {
  const response = await apiClient.post('https://api.nevua.markets/watchlists', {
    watchListIds: []
  });

  if (response.status !== 200) {
    throw new Error('Failed to fetch watchlists');
  }

  const data: GetWatchListJSONResponse = response.data;
  const watchlists = data.watchLists;

  // Sort watchlists by createdAt descending
  watchlists.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  store.watchLists = [...watchlists];
}

export async function loadUserSettings(token: string) {
  if (!token) {
    console.error('No token received from server');
    return;
  }

  store.isAuthenticated = true;
  const decoded = jwtDecode<TokenPayload>(token);

  store.email = decoded.email;
  store.id = decoded.id;

  // flags
  try {
    const response = await apiClient.get('https://api.nevua.markets/users/flags');
    if (response.status === 200) {
      const flags = response.data;
      if (flags?.showWelcomeModal) {
        store.showWelcomeModal = true;
        await apiClient.post('https://api.nevua.markets/users/flags', {
          showWelcomeModal: false
        });
      }
    }
  } catch (error) {
    Sentry.setExtra('method', 'loadUserSettings::fetchUserFlags');
    Sentry.captureException(error);
  }

  // Order matters here

  await fetchWatchLists();

  // Check if there is a watchlist in local storage, and if so:
  // 1. save it in API, 
  // 2. unshift it and 
  // 3. delete from local storage
  // 4. push route to it
  const watchList = localStorage.getItem('watchList');
  if (watchList) {
    let watchListObject;
    try {
      watchListObject = JSON.parse(watchList);
    } catch (error) {
      Sentry.setExtra('method', 'loadUserSettings::createWatchListInAPI::JSON.parse');
      Sentry.setExtra('watchList', watchList);
      Sentry.captureException(error);
      localStorage.removeItem('watchList');
      return;
    }
    const newWatchListId = await createWatchListInAPI(watchListObject);
    if (newWatchListId) {
      console.log('Created watchlist in API');
      watchListObject.id = newWatchListId;
      store.watchLists.unshift(watchListObject);
      localStorage.removeItem('watchList');
      router.push(`/l/${newWatchListId}`);
    }
  }


  //await fetchSubscriptionsToGlobalState();
  //await fetchWatchLists();
  //await fetchNotifications();
}

export function resetStore() {
  Object.assign(store, _.cloneDeep(DEFAULT_STORE_SETTINGS))
}

/**
 * Check if the user is authenticated by verifying the presence of a JWT token.
 * @returns {boolean} - Returns true if a token is present, false otherwise.
 */
export function hasToken(): boolean {
  const token = localStorage.getItem('jwtToken');
  return !!token;
}

export async function initializeSettings() {
  if (!hasToken()) {
    store.isAuthenticated = false;
    // Clears store to initial state
    resetStore();

    // Check if there is a watchlist in local storage
    const watchList = localStorage.getItem('watchList');
    
    if (watchList) {
      try {
        store.watchLists = [JSON.parse(watchList)];
      } catch (error) {
        Sentry.setExtra('method', 'initializeSettings::loadUserSettings::JSON.parse');
        Sentry.captureException(error);
        localStorage.removeItem('watchList');
      }
    }

    return false;
  }
  else {
    let isAuthenticated = await confirmAuth();
    const token = localStorage.getItem('jwtToken');
    if (isAuthenticated && token) {
      await loadUserSettings(token);
    }
  }
}

export function resetTableStateEvents() {
  store.tableState.events = [];
  store.tableState.rowCount = 0;
  store.tableState.multiMarketEvents = {} as Record<string, { expanded: boolean }>;
}

export const deleteWatchList = useDebounceFn(async (id: string) => {
  if (!id) {
    console.warn('No id provided to deleteWatchList');
    return;
  }

  if (!store.currentWatchListId || !store.isAuthenticated) {
    return true;
  }

  let response = await apiClient.post('https://api.nevua.markets/watchlists/delete', {
    watchListId: id
  });

  if (response.status !== 200) {
    console.error('Failed to delete watchlist');
    return false;
  }



  return true;
}, 1000);

export function getWatchListPath(item: WatchListWithId) {
  return `/l/${item.id}`;
}

export function forceTableRefresh() {
  // Create new references to force reactivity
  store.tableState = {
    ...store.tableState,
    events: [...store.tableState.events]
  };
}

export function parseMarketClobTokenId(market) {
  let parsedClobTokenIds = []
  try {
    if (!market.clobTokenIds) {
      console.warn(`No clobTokenIds found for market ${market.id} and question ${market.question}`);
      return []
    }
    parsedClobTokenIds = JSON.parse(market.clobTokenIds);
    if (!Array.isArray(parsedClobTokenIds)) {
      throw new Error('clobTokenIds is not an array');
    }

  } catch (error) {
    Sentry.setExtra('method', 'parseMarketClobTokenId');
    Sentry.setExtra('market', market);
    Sentry.captureException(error);
    console.error('Error parsing clobTokenIds:', error);
    return []
  }
  return parsedClobTokenIds;
}

// Ok
export function removeEventFromWatchList(watchlist, eventId) {
  if (watchlist.events[eventId]) {
    delete watchlist.events[eventId];
  }
}

// Ok
export function removeFromTableState(eventId: string, marketId: string | null = null) {
  const eventIndex = store.tableState.events.findIndex(e => e.id === eventId);
  if (eventIndex === -1) {
    return;
  }

  // If no marketId is provided, remove the entire event
  if (marketId === null) {
    store.tableState.events.splice(eventIndex, 1);
    if (store.tableState.multiMarketEvents[eventId]) {
      delete store.tableState.multiMarketEvents[eventId];
    }
    return;
  }

  const event = store.tableState.events[eventIndex];
  const updatedMarkets = event.markets.filter(m => m.id !== marketId);

  if (updatedMarkets.length === 0) {
    // If no markets are left, remove the entire event
    store.tableState.events.splice(eventIndex, 1);
  } else {
    // Create new event object with filtered markets to maintain reactivity
    store.tableState.events[eventIndex] = {
      ...event,
      markets: updatedMarkets
    };
  }
}

export async function removeContextItemsFromList(currentWatchList: WatchListWithId, contextMenu: any, selectedRows: any): Promise<{ removedMarkets: number, removedEvents: number }> {
  if (!currentWatchList) {
    throw new Error('No currentWatchList provided to removeContextItemsFromList');
  };

  let selectedByEvent = {};

  if (contextMenu.isBulkAction) {
    // Group selected rows by event
    Object.entries(selectedRows).forEach(([key, isSelected]) => {
      if (isSelected) {
        const [eventId, marketId] = key.split('-');
        if (!selectedByEvent[eventId]) {
          selectedByEvent[eventId] = { markets: [] };
        }
        if (marketId !== 'parent') {
          selectedByEvent[eventId].markets.push(marketId);
        }
      }
    });
  } else {
    // Handle single item removal, event item
    if (contextMenu.isParentRow) {
      const eventId = contextMenu.selectedEvent.id;
      selectedByEvent = {
        [eventId]: {
          markets: store.tableState.events.find(e => e.id === eventId)?.markets.map(m => m.id) || []
        }
      };
      // Handle single item removal, market item
    } else {
      const eventId = contextMenu.selectedEvent.id;
      const marketId = contextMenu.selectedMarket.id;
      selectedByEvent = {
        [eventId]: {
          markets: [marketId]
        }
      };
    }
  }

  const { removedMarkets, removedEvents } = await removeSelectedFromList(currentWatchList, selectedByEvent);
  return { removedMarkets, removedEvents };

}
// Ok
export function createNewWatchListPlaceholderItem(name: string) {
  return {
    name: name,
    events: {},
    createdAt: new Date().toISOString(),
    tableSettings: {
      showClosedItems: true,
      expandMultiMarketEventsTable: true,
      expandMultiMarketEventsTiles: false
    }
  };
}
// Ok
export function addContextItemsToWatchList(targetWatchList: WatchListWithId, contextMenu: any, selectedRows: any): number {
  let selectedByEvent = {};

  // Bulk action
  if (contextMenu.isBulkAction) {
    Object.entries(selectedRows).forEach(([key, isSelected]) => {
      if (isSelected) {
        const [eventId, marketId] = key.split('-');
        if (marketId === 'parent') {
          return;
        }

        // TODO: smarter check for autofetch, currently if event has multiple markets,
        // it will not be autofetched
        // Instead, we need to check if all items of an event are selected
        const event = store.tableState.events.find(e => e.id === eventId);
        if (!event) {
          console.warn('Event not found', eventId);
          return;
        }
        if (!selectedByEvent[eventId]) {
          selectedByEvent[eventId] = {
            autoFetch: event.nevuaMultipleMarkets ? false : true,
            markets: []
          };
        }
        selectedByEvent[eventId].markets.push(marketId);
      }
    });

    // Right click
  } else {
    const eventId = contextMenu.selectedEvent.id;
    if (contextMenu.isParentRow) {
      // Selecting the parent event means we want to add all markets of this event from the source watchlist
      // If parent row i.e. event has many markets, autoFetch is false
      const event = store.tableState.events.find(e => e.id === eventId);
      if (event) {
        selectedByEvent[eventId] = {
          autoFetch: false,
          markets: event.markets.map(m => m.id)
        };
      }
    } else {
      // If not parent row i.e. event has only one market, autoFetch is true
      selectedByEvent[eventId] = {
        autoFetch: true,
        markets: [contextMenu.selectedMarket.id]
      };
    }
  }
  const addedCount = addSelectedToWatchList(targetWatchList, selectedByEvent);

  return addedCount;
}
// Ok
export async function removeSelectedFromList(
  currentWatchList: WatchListWithId,
  selectedByEvent: Record<string, { markets: string[] }> = {}
): Promise<{ removedMarkets: number, removedEvents: number }> {
  if (!currentWatchList) return { removedMarkets: 0, removedEvents: 0 };

  let removedMarkets = 0;
  let removedEvents = 0;

  // Process each event
  Object.entries(selectedByEvent).forEach(([eventId, data]) => {
    const event = store.tableState.events.find(e => e.id === eventId);
    if (!event) return;


    const marketsToRemove = event.markets.filter(market => data.markets.includes(market.id || ''));

    if (marketsToRemove.some(market => market.active && market.closed === false)) {
      // Only disabled autoFetch if at least one active & open market is being removed
      currentWatchList.events[eventId].autoFetch = false;
    }
    currentWatchList.events[eventId].markets = currentWatchList.events[eventId].markets.filter(id => !data.markets.includes(id));

    if (currentWatchList.events[eventId].markets.length === 0) {
      removeEventFromWatchList(currentWatchList, eventId);
      removeFromTableState(eventId);
      removedEvents++;
      removedMarkets += data.markets.length;
    }
  });

  store.tableState.rowCount = Math.max(0, store.tableState.rowCount - removedEvents);

  return { removedMarkets, removedEvents };
}
// Ok
export function addSelectedToWatchList(
  targetWatchList: WatchListWithId,
  selectedByEvent: Record<string, { autoFetch?: boolean, markets: string[] }> = {}
) {
  let addedCount = 0;

  // Process each event
  Object.entries(selectedByEvent).forEach(([eventId, data]) => {
    if (!eventId) {
      console.warn('No eventId provided to addSelectedToWatchList');
      return;
    }

    if (!targetWatchList) {
      console.warn('Undefined targetWatchList');
      return
    }

    if (targetWatchList?.events?.[eventId]?.autoFetch) {
      // If target watchlist already has autoFetch enabled for this event, skip
      return;
    }

    const existingMarkets = targetWatchList?.events?.[eventId]?.markets || [];

    let autoFetchValue = false;
    if (data?.autoFetch !== undefined) {
      autoFetchValue = data.autoFetch;
    } else {
      autoFetchValue = true;
    }

    // Add the selected markets to the target watchlist
    targetWatchList.events[eventId] = {
      autoFetch: autoFetchValue,
      markets: [...new Set(existingMarkets.concat(data.markets))]
    }
    addedCount++;
  })
  return addedCount;
}

export async function updateWatchListQueryAndEvents(events: EventsMap, query: WatchlistQuery) {
  if (!store.currentWatchListId) {
    return;
  }

  const watchlist = store.watchLists.find(watchList => watchList.id === store.currentWatchListId);
  if (!watchlist) {
    return;
  }
  
  // create a copy of the current watchlist
  const updatedWatchList: Partial<WatchListWithId> = {  
    queries: [...watchlist?.queries || []],
    events: events,
  };

  // update the query
  if (!updatedWatchList.queries) {
    updatedWatchList.queries = [];
  }
  updatedWatchList.queries.unshift(query);
  
  // attempt to update the watchlist in the API
  if (!store.isAuthenticated) {
    watchlist.queries = updatedWatchList.queries;
    watchlist.events = events;

    console.log('Saving to local storage');
    localStorage.setItem('watchList', JSON.stringify(watchlist));

    // Only keep a single watchlist in store
    store.watchLists = [watchlist];
    return watchlist.id;
  }
  
  const success = await updateWatchList(watchlist.id, updatedWatchList);
  if (!success) {
    showDialog({
      message: 'Failed to update watchlist',
      type: 'error',
      timeout: 3000
    });
    return false;
  }

  showDialog({
    message: 'Watchlist updated',
    type: 'success',
    timeout: 3000
  });

  // update the store
  watchlist.queries = updatedWatchList.queries;
  watchlist.events = events;

  return true;
}

export async function saveSearchResultsToNewWatchList(searchResults: MatchingEvent[], query: WatchlistQuery): Promise<string | null> {
  const newWatchList: WatchListWithId = {
    ...createNewWatchListPlaceholderItem(query.text.trim()),
    id: generateUUID(),
    createdAt: new Date().toISOString(),
    queries: [query]
  };

  for (const result of searchResults) {
    addSelectedToWatchList(newWatchList, {
      [result.id]: {
        autoFetch: result.type === 'event' ? true : false,
        markets: result.markets.map(m => m.id)
      }
    });
  }
  // if unauthenticated, save to local storage
  if (!store.isAuthenticated) {
    console.log('Saving to local storage');
    localStorage.setItem('watchList', JSON.stringify(newWatchList));

    // Only keep a single watchlist in store
    store.watchLists = [newWatchList];
    return newWatchList.id;
  } else {
    // If authenticated, save to API
    let watchListId = await createWatchListInAPI(newWatchList);

    // New watchlist have an event & market status subscription created for them
    await fetchSubscriptionsToGlobalState();

    if (watchListId) {
      newWatchList.id = watchListId;
      store.watchLists.unshift(newWatchList);

      // Show success notification
      showDialog({
        message: `Created new watchlist from search results`,
        type: 'success',
        timeout: 3000
      });
      return watchListId;
    } else {
      showDialog({
        message: `Failed to create watchlist`,
        type: 'error',
        timeout: 3000
      });
      return null;
    }
  }
}

  interface TokenPricesHistoryRequest {
    events: {
      [eventId: string]: {
        markets: string[]
      }
    }
  }

  export function getEventsMap(watchList: WatchListWithId, eventIdsFilter: string[] = []): TokenPricesHistoryRequest {
    return Object.entries(watchList?.events || {}).reduce((acc, [eventId, event]) => {
      if (eventIdsFilter.length > 0 && !eventIdsFilter.includes(eventId)) {
        return acc;
      }
      const uniqueMarkets = [...new Set(event.markets)];
      if (uniqueMarkets.length > 0) {
        acc.events[eventId] = { markets: uniqueMarkets };
      }
      return acc;
    }, { events: {} });
  }

  export function autoHideTippy(instance) {
    setTimeout(() => {
      instance.hide();
    }, 5000);
  }

  function generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  export function showDialog(options: Partial<DialogConfig>) {
    const id = generateUUID();
    store.dialogs.push({
      id,
      message: options.message || '',
      title: options.title,
      type: options.type || 'info',
      timeout: options.timeout || 5000
    })
    
    setTimeout(() => {
      removeDialog(id);
    }, options.timeout);
    
  }

  // export function showNotification(options: Partial<NotificationResponse>) {
  //   const id = generateUUID();
  //   store.notifications.push({
  //     id,
  //     message: options.message || '',
  //     title: options.title,
  //     type: options.type || 'info',
  //     timeout: options.timeout || 5000
  //   });

  //   // Show notifications when a new one is added
  //   store.showNotifications = true;
  // }

  export function toggleNotifications() {
    store.showNotifications = !store.showNotifications;

    // Add a flag to indicate manual toggle.
    // This is mainly to prevent automatic notification hiding when user has clicked to show notifications.

    // Users that want to hide notification popups should configure it in the subscription settings
    if (store.showNotifications) {
      store.notificationsManuallyToggled = true;
    } else {
      store.notificationsManuallyToggled = false;
    }
  }

  export function unreadNotificationsCount() {
    const unreadNotifications = store.notifications.filter(notification => {
      const inAppChannel = notification?.channels?.find(channel => channel.type === NotificationChannelType.IN_APP);
      return inAppChannel && inAppChannel?.status !== 'READ';
    });
    return unreadNotifications.length;
  }

  export function removeDialog(id: string) {
    store.dialogs = store.dialogs.filter(dialog => dialog.id !== id)
  }

  //export async function fetchSubscriptionsToTableState(listId?: string, isEventTable: boolean = true): Promise<SubscriptionResponse[]> {
  export async function fetchSubscriptionsToGlobalState() {
    if (!store.isAuthenticated) {
      return;
    }

    try {
      //let subscriptions_response;
      //if (listId) {
      //subscriptions_response = await apiClient.get(`https://api.nevua.markets/subscriptions/${listId}`);   
      //} else {
      const subscriptions_response = await apiClient.get(`https://api.nevua.markets/subscriptions`);
      //}

      if (subscriptions_response.status !== 200) {
        console.error('Error fetching subscriptions:', subscriptions_response.data);
        return [];
      }

      const subscriptions = subscriptions_response.data;

      // Reset state since we are fetching all subscriptions for listId
      //store.tableState.nevuaSubscriptions = [];

      store.nevuaSubscriptions = [];
      for (const subscription of subscriptions.subscriptions) {
        store.nevuaSubscriptions.push(subscription);
      }

    } catch (error) {
      Sentry.setExtra('method', 'fetchSubscriptionsToGlobalState');
      Sentry.setExtra('userId', store.id);
      Sentry.captureException(error);
      console.error('Error fetching subscriptions:', error);
    }
  }

  // This function is necessary to show the alerts bells in events that have a subscription
  export function addSubscriptionToTableState(subscription: SubscriptionResponse, currentWatchListId: string) {
    if (currentWatchListId !== subscription?.watchlistId) {
      // Do not update table state if the subscription is not in the current watchlist
      return;
    }

    // Add to the appropriate scope
    switch (subscription.subscriptionScope) {
      case 'Market': {
        const event = store.tableState.events.find(item => item.markets.some(market => market.id === subscription.marketId));
        if (!event) {
          console.warn('Market not found for subscription', subscription.marketId);
          return;
        }
        const market = event.markets.find(market => market.id === subscription.marketId) as (MarketSchema & { nevuaSubscriptions?: SubscriptionResponse[] });
        if (!market.nevuaSubscriptions) {
          market.nevuaSubscriptions = [];
        }
        market.nevuaSubscriptions.push(subscription);
        break;
      }
      case 'Event': {
        const event = store.tableState.events.find(item => item.id === subscription.eventId) as (EventSchema & { nevuaSubscriptions?: SubscriptionResponse[] });
        if (!event) {
          console.warn('Event not found for subscription', subscription.eventId);
          return;
        }
        if (!event.nevuaSubscriptions) {
          event.nevuaSubscriptions = [];
        }
        event.nevuaSubscriptions.push(subscription);
        break;
      }
      case 'Watchlist': {
        // No additional action needed for watchlist subscriptions
        break;
      }
    }
  }

  export function addSubscriptionInGlobalStateAndTableState(subscription: SubscriptionResponse, currentWatchListId: string) {
    // Add to the main array
    store.nevuaSubscriptions.push(subscription);
    addSubscriptionToTableState(subscription, currentWatchListId);
  }

  export function deleteSubscriptionFromGlobalStateAndTableState(subscriptionId: string, currentWatchListId: string) {
    // Find the subscription to get its scope
    const subscription = store.nevuaSubscriptions.find(sub => sub.subscriptionId === subscriptionId);
    if (!subscription) {
      console.warn('Subscription not found', subscriptionId);
      return;
    }

    const scope = subscription.subscriptionScope;

    // Remove from main subscriptions array
    store.nevuaSubscriptions = store.nevuaSubscriptions.filter(
      sub => sub.subscriptionId !== subscriptionId
    );

    if (currentWatchListId !== subscription?.watchlistId) {
      // Do not update table state if the subscription is not in the current watchlist
      return;
    }

    switch (scope) {
      case 'Market': {
        // Find and remove from market's subscriptions
        const event = store.tableState.events.find(item =>
          item.markets.some(market => market.id === subscription.marketId)
        );
        if (event) {
          const market = event.markets.find(market => market.id === subscription.marketId) as (MarketSchema & { nevuaSubscriptions?: SubscriptionResponse[] });
          if (market?.nevuaSubscriptions) {
            market.nevuaSubscriptions = market.nevuaSubscriptions.filter(
              sub => sub.subscriptionId !== subscriptionId
            );
          }
        }
        break;
      }
      case 'Event': {
        // Find and remove from event's subscriptions
        const event = store.tableState.events.find(item => item.id === subscription.eventId) as (EventSchema & { nevuaSubscriptions?: SubscriptionResponse[] });
        if (event?.nevuaSubscriptions) {
          event.nevuaSubscriptions = event.nevuaSubscriptions.filter(
            sub => sub.subscriptionId !== subscriptionId
          );
        }
        break;
      }
      case 'Watchlist': {
        // No additional cleanup needed for watchlist subscriptions
        break;
      }
    }
  }

  export async function updateSubscriptionInGlobalStateAndTableState(updatedSubscription: SubscriptionResponse, currentWatchListId: string) {
    // First delete the old subscription using its ID
    deleteSubscriptionFromGlobalStateAndTableState(updatedSubscription.subscriptionId, currentWatchListId);
    addSubscriptionInGlobalStateAndTableState(updatedSubscription, currentWatchListId);
  }

  export async function fetchNotifications() {
    if (!store.isAuthenticated) {
      return;
    }

    store.notifications = [];

    let hasMore = true;
    let before: string | null = null;

    while (hasMore) {
      const response = await apiClient.get(`https://api.nevua.markets/notifications`, { params: { before } });
      if (response.status !== 200) {
        console.error('Error fetching notifications:', response.data);
        return;
      }
      const data: GetNotificationsJSONResponse = response.data;

      store.notifications.push(...data.notifications);

      if (data.hasMore) {
        before = data.nextCursor;
      } else {
        hasMore = false;
      }
    }
  }

  export async function pauseAlertInAPI(subscription) {
    try {
      const response = await apiClient.post(`https://api.nevua.markets/subscriptions/${subscription.subscriptionId}/pause`);
      if (response.status !== 200) {
        throw new Error('Failed to pause alert')
      }
      showDialog({
        message: 'Alert paused successfully',
        type: 'success',
        timeout: 3000
      });

      // Update subscriptions table
      if (store.currentWatchListId) {
        await updateSubscriptionInGlobalStateAndTableState(response.data.subscriptions[0], store.currentWatchListId);
      }
    } catch (error) {
      Sentry.setExtra('method', 'pauseAlertInAPI');
      Sentry.setExtra('subscriptionId', subscription.subscriptionId);
      Sentry.captureException(error);
      showDialog({
        message: 'Failed to pause alert',
        type: 'error',
        timeout: 3000
      });
    }
  }

  export async function startAlertInAPI(subscription, watchlistId) {
    try {

      const response = await apiClient.post(`https://api.nevua.markets/subscriptions/${subscription.subscriptionId}/restart`);
      if (response.status !== 200) {
        throw new Error('Failed to start alert')
      }
      showDialog({
        message: 'Alert started successfully',
        type: 'success',
        timeout: 3000
      });

      // Update subscriptions table
      if (_.size(watchlistId) > 0) {
        await updateSubscriptionInGlobalStateAndTableState(response.data.subscriptions[0], watchlistId);
      }

    } catch (error) {
      Sentry.setExtra('method', 'startAlertInAPI');
      Sentry.setExtra('subscriptionId', subscription.subscriptionId);
      Sentry.captureException(error);
      showDialog({
        message: 'Failed to start alert',
        type: 'error',
        timeout: 3000
      });
    }
  }

  export function canStartAlert(subscription) {
    if (subscription.status.enabled) {
      return true;
    }

    if (subscription.status.reason === SubscriptionStatusReason.EVENT_CLOSED) {
      return false;
    }

    if (subscription.status.reason === SubscriptionStatusReason.MARKET_CLOSED) {
      return false;
    }

    if (subscription.expiresAt && new Date(subscription.expiresAt) < new Date()) {
      return false;
    }

    if (subscription.channels.length === 0) {
      return false;
    }

    return true;
  }

  export function markAllNotificationsAsRead(notifications: NotificationResponse[] = []) {
    // If list is empty, mark all as read
    if (_.size(notifications) === 0) {
      store.notifications.forEach(notification => {
        notification.channels.forEach(channel => {
          if (channel.type === NotificationChannelType.IN_APP && channel.status !== 'READ') {
            notificationIdsToMarkAsRead.push(notification.id);
            channel.status = 'READ';
          }
        });
      });
    } else {
      // Only mark selected notifications as read
      notifications.forEach(notification => {
        notification.channels.forEach(channel => {
          if (channel.type === NotificationChannelType.IN_APP && channel.status !== 'READ') {
            notificationIdsToMarkAsRead.push(notification.id);
            channel.status = 'READ';
          }
        });
      });
    }
  }

  export function markNotificationAsRead(id: string) {
    const notification = store.notifications.find(notification => notification.id === id)

    if (notification) {
      notification.channels.forEach(channel => {
        if (channel.type === NotificationChannelType.IN_APP && channel.status !== 'READ') {
          notificationIdsToMarkAsRead.push(notification.id);
          channel.status = 'READ';
        }
      });
    }
  }

  // Function to handle marking notifications as read in the API
  async function markNotificationsAsReadInAPI() {
    if (notificationIdsToMarkAsRead.length === 0) {
      return;
    }

    // Create a copy of the IDs to process
    const idsToProcess = [...notificationIdsToMarkAsRead];

    // clear notificationIdsToMarkAsRead array without re-assigning it
    notificationIdsToMarkAsRead.splice(0, notificationIdsToMarkAsRead.length);

    try {
      const response = await apiClient.patch('https://api.nevua.markets/notifications', {
        notificationIds: idsToProcess
      });

      if (response.status !== 200) {
        console.error('Failed to mark notifications as read', response.data);
        notificationIdsToMarkAsRead.push(...idsToProcess);
      }
    } catch (error) {
      Sentry.setExtra('method', 'markNotificationsAsReadInAPI');
      Sentry.setExtra('userId', store.id);
      Sentry.captureException(error);
      notificationIdsToMarkAsRead.push(...idsToProcess);
    }
  }

  export async function deleteNotificationsInAPI(notificationIds: string[]) {
    try {
      const response = await apiClient.post('https://api.nevua.markets/notifications', {
        action: 'delete',
        notificationIds: notificationIds
      });

      if (response.status !== 200) {
        throw new Error('Failed to delete notifications');
      }
    } catch (error) {
      Sentry.setExtra('method', 'deleteNotificationsInAPI');
      Sentry.setExtra('userId', store.id);
      Sentry.captureException(error);
    }
  }

  /**
   * Extracts Polymarket event slugs from raw text containing URLs
   * Handles common URL variations and protocol typos
   */
  export function extractSlugs(rawInput: string): string[] {
    const slugSet = new Set<string>();

    // Regex explanation:
    // 1. Match optional protocol with common typos (http, https, ttp, tps, etc)
    // 2. Match optional www subdomain
    // 3. Match required /event/ path segment
    // 4. Capture slug (letters, numbers, hyphens) until next /, ?, #, or end
    const regex = /(?:https?:\/\/|tps?:\/\/|htps?:\/\/)?(?:www\.)?polymarket\.com\/event\/([a-z0-9-]+)(?=[\/?#]|$)/gi;

    let match: RegExpExecArray | null;

    while ((match = regex.exec(rawInput)) !== null) {
      if (match[1]) {
        // Normalize to lowercase for consistency
        slugSet.add(match[1].toLowerCase());
      }
    }

    return Array.from(slugSet);
  }

  const testCases = [
    {
      input: 'https://polymarket.com/event/what-will-trump-say-this-week-322?tid=1744625273373',
      expected: ['what-will-trump-say-this-week-322']
    },
    {
      input: 'ttp://polymarket.com/event/election-2024-presidential-winner-891',
      expected: ['election-2024-presidential-winner-891']
    },
    {
      input: `Multiple URLs:
    https://polymarket.com/event/slug-one
    tps://www.polymarket.com/event/slug_two
    polymarket.com/event/slug3`,
      expected: ['slug-one', 'slug_two', 'slug3']
    },
    {
      input: 'Invalid: https://polymarket.com/not-event/skip-this',
      expected: []
    },
    {
      input: 'Case variations: HTTPS://WWW.POLYMARKET.COM/EVENT/UPPERCASE-SLUG',
      expected: ['uppercase-slug']
    }
  ];

  async function testExtactSlugs() {

    let passed = 0;

    for (const [index, tc] of testCases.entries()) {
      try {
        const result = extractSlugs(tc.input);
        if (_.isEqual(result, tc.expected)) {
          passed++;
          console.log(`✓ Test case ${index + 1}`);
        } else {
          console.log(`✗ Test case ${index + 1}`);
          console.error(result, tc.expected);
        }
      } catch (err) {
        console.log(`✗ Test case ${index + 1}`);
        console.error(err);
      }
    }

    console.log(`\nResults: ${passed}/${testCases.length} passed`);
  }


  export async function updatePrices() {
    try {
      const listId = store.currentWatchListId;
      if (!listId) {
        return;
      }

      const watchList = store.watchLists.find(item => item.id === listId);
      if (!watchList) {
        return;
      }

      const eventsMap = getEventsMap(watchList, []);

      const response = await apiClient.post('https://api.nevua.markets/markets/price-updates', eventsMap)
      if (response.status !== 200) {
        console.error('Error updating prices:', response.status, response.data);
        return;
      }

      const priceUpdates: EventPriceData[] = response.data;

      // Now loop through the priceUpdates and update the table state
      for (const item of priceUpdates) {
        const event = store.tableState.events.find(e => e.id === item.id);
        if (event) {
          let isEventUpdated = false;
          if (event.volume && item.volume && event.volume !== item.volume) {
            event.volume = item.volume;
            isEventUpdated = true;
          }
          if (event.active && item.active && event.active !== item.active) {
            event.active = item.active;
            isEventUpdated = true;
          }
          if (event.closed && item.closed && event.closed !== item.closed) {
            event.closed = item.closed;
            isEventUpdated = true;
          }

          //if (isEventUpdated) {
            //if (event.nevuaMultipleMarkets) {
              // event.nevuaLastUpdatedTimestamp = Math.floor(new Date().valueOf() / 1000);
              // store.tableState.updatedRowKeys[`${event.id}-parent`] = true;
              // setTimeout(() => delete store.tableState.updatedRowKeys[`${event.id}-parent`], 1000);
            //}
          //}

          // Now loop through the markets and update the table state
          for (const market of item.markets) {

            const eventMarket = event.markets.find(m => m.id === market.id);

            if (eventMarket) {
              let isMarketUpdated = false;

              if (eventMarket?.volume && market?.volume && eventMarket.volume !== market.volume) {
                eventMarket.volume = market.volume;
                isMarketUpdated = true;
              }
              if (eventMarket.active && market.active && eventMarket.active !== market.active) {
                eventMarket.active = market.active;
                isMarketUpdated = true;
              }
              if (eventMarket.closed && market.closed && eventMarket.closed !== market.closed) {
                eventMarket.closed = market.closed;
                isMarketUpdated = true;
              }
              if (eventMarket?.outcomePrices && market?.outcomePrices && eventMarket.outcomePrices !== market.outcomePrices) {
                eventMarket.outcomePrices = market.outcomePrices;
                isMarketUpdated = true;
              }

              //if (isMarketUpdated) {
                // Markets don't have separate nevuaLastUpdatedTimestamp, so we use the event's
                event.nevuaLastUpdatedTimestamp = Math.floor(new Date().valueOf() / 1000);
                //store.tableState.updatedRowKeys[`${event.id}-${market.id}`] = true;
                //setTimeout(() => delete store.tableState.updatedRowKeys[`${event.id}-${market.id}`], Math.random() * (2000 - 1000) + 1000);
                // nextTick(() => {
                //   delete store.tableState.updatedRowKeys[`${event.id}-${market.id}`];
                // });
              //}
            }
          }
        }
      }
    } catch (error) {
      Sentry.setExtra('method', 'updatePrices');
      Sentry.captureException(error);
    }
  }

  /*
    Subscription Dialog Actions
  */

  export function clearAlertFilters() {
    store.listAlertsForMarketId = null;
    store.listAlertsForEventId = null;
    store.listAlertsForWatchlistId = null;
  }

  export function clearSubscriptionDialogState() {
    store.showSubscriptionsDialog = false;

    store.subscriptionScope = null;
    store.subscriptionToEdit = null;
    store.subscriptionTargetTitle = null;
    store.eventsToCreateAlertFor = null;
    store.listAlertsForMarketId = null;
    store.listAlertsForEventId = null;
    store.listAlertsForWatchlistId = null;
    store.subscriptionIdToDelete = null;
    store.showAlertListTabFirst = false;
    store.subscriptionPrice = null;
  }

  export function handleCreateAlert(scope = SubscriptionScope.WATCHLIST, event, market, price = null) {
    const selectedEvent = event;
    const selectedMarket = market;

    const selectedEventId = selectedEvent?.id;
    const selectedMarketId = selectedMarket?.id;

    if (price !== null) {
      store.subscriptionPrice = price;
    }

    if (!selectedEventId && scope !== SubscriptionScope.WATCHLIST) {
      console.warn('No event selected')
      return;
    }

    if (!selectedMarketId && scope === SubscriptionScope.MARKET) {
      console.warn('No market selected')
      return;
    }

    if (!_.size(store.currentWatchListId)) {
      console.warn('No watchlist selected')
      return;
    }

    store.subscriptionScope = scope;
    store.subscriptionTargetTitle = null;
    store.eventsToCreateAlertFor = {};

    switch (scope) {
      case SubscriptionScope.WATCHLIST:
        store.subscriptionTargetTitle = store.currentWatchList.name;
        // We pass an empty object for eventsToCreateAlertFor because 
        // we want to create alerts for all events in the watchlist
        break;
      case SubscriptionScope.EVENT:
        store.subscriptionTargetTitle = selectedEvent.title;
        // this.listAlertsForEventId = selectedEventId;
        // this.listAlertsForMarketId = null;

        store.eventsToCreateAlertFor[selectedEventId] = _.pick(store.currentWatchList.events[selectedEventId] || {
          markets: [],
        }, ['markets']);
        break;
      case SubscriptionScope.MARKET:
        store.subscriptionTargetTitle = selectedMarket.question;
        // this.listAlertsForEventId = selectedEventId;
        // this.listAlertsForMarketId = selectedMarketId;

        store.eventsToCreateAlertFor[selectedEventId] = {
          markets: [selectedMarketId],
        };
        break;
    }
    store.showSubscriptionsDialog = true;
  }

  // Handles clicked 'Create Watchlist alert' button
  export function handleCreateWatchlistAlert(scope = SubscriptionScope.WATCHLIST) {
    clearSubscriptionDialogState();
    nextTick(() => {
      handleCreateAlert(scope, null, null);
    });
  }

  /*
    Periodic actions
  */

  // Mark notifications as read
  setInterval(markNotificationsAsReadInAPI, ms('5s')); 

  // Update prices
  //setInterval(updatePrices, ms('2m')); 


/*
  ------------------
*/

export function getOutcomes(notification: NotificationResponse) {
  //type guard
  function isEventStatusNotification(notification: NotificationResponse): notification is EventStatusNotificationResponse {
    return notification.type === 'event_status_notification';
  }

  try {
    if (isEventStatusNotification(notification)) {
      const outcomes = JSON.parse(notification.outcomes || '[]');
      let outcomePrices = JSON.parse(notification.outcomePrices || '[]');

      if (outcomes.length === 0 || outcomePrices.length === 0) {
        return ''
      }

      outcomePrices = outcomePrices.map(price => (Number(price) * 100).toFixed(0));
      
      return `${outcomes[0]} ${outcomePrices[0]}%, ${outcomes[1]} ${outcomePrices[1]}%`
    }
  } catch (error) {
    Sentry.setExtra('method', 'getOutcomes');
    Sentry.captureException(error);
    return ''
  }

}